<template>
  <v-card class="pa-2" outlined tile height="100%">
    <v-snackbar v-model="msg" top :vertical="true">
      {{ text }}
      <template v-slot:action="{ attrs }">
        <v-btn dark :color="color" text v-bind="attrs" @click="msg = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <v-data-table
      v-bind="tableProps"
      :headers="isPageHistory ? headers : unsignedHeaders"
      :loading="isLoading"
      :search="search"
      :items-per-page="itemsPerPage"
      height="515"
      ref="table"
      class="mt-2"
    >
      <template v-slot:group.header="{ group, headers, toggle, isOpen }">
        <td :colspan="headers.length">
          <v-btn @click="toggle" x-small icon :ref="group">
            <v-icon v-if="isOpen">mdi-chevron-up</v-icon>
            <v-icon v-else>mdi-chevron-down</v-icon>
          </v-btn>
          <span class="font-weight-bold">{{ group }}</span>
        </td>
      </template>

      <template v-slot:top>
        <v-row>
          <v-col cols="12" md="6">
            <v-toolbar flat color="white">
              <v-spacer></v-spacer>
              <v-toolbar-title>{{
                isPageHistory ? "Completed MARs" : "Unsigned MARs"
              }}</v-toolbar-title>
              <v-divider class="mx-4" inset vertical></v-divider>
              <v-text-field
                v-model="search"
                clearable
                flat
                solo-inverted
                hide-details
                prepend-inner-icon="search"
                label="search"
              ></v-text-field>

              <MarSignDialog
                ref="marDialog"
                :dialog="dialog"
                @close="dialog = false"
                @updateMAR="updateMAR"
                @verifiedBySignature="verifiedBySignature"
                :verifiedSignature="newVerifiedBy"
                :isPageHistory="isPageHistory"
                :unsigned="unsigned"
                :isAuditorRole="isAuditorRole"
              />
              <v-spacer></v-spacer>
            </v-toolbar>
          </v-col>

          <v-col cols="12" md="6">
            <v-row class="mr-2">
              <v-col cols="12" md="5" sm="6">
                <v-menu
                  ref="menu"
                  v-model="startMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="startDate"
                      label="Start Date"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      filled
                      dense
                      clearable
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="startDate"
                    no-title
                    scrollable
                    @input="startMenu = false"
                  >
                  </v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="5" sm="6">
                <v-menu
                  ref="menu"
                  v-model="endMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="endDate"
                      label="End Date"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      filled
                      dense
                      clearable
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="endDate"
                    no-title
                    scrollable
                    @input="endMenu = false"
                  >
                  </v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="2" sm="12">
                <v-btn
                  block
                  class="mb-10 mt-1"
                  color="primary"
                  large
                  @click="getMARS"
                  >search</v-btn
                >
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-icon small class="mr-2 hidden-sm-and-down" @click="viewMAR(item)">
          mdi-eye
        </v-icon>

        <v-btn
          outlined
          medium
          class="mr-4 hidden-sm-and-up blue--text"
          @click="viewMAR(item)"
        >
          edit
        </v-btn>

        <v-icon
          small
          class="hidden-sm-and-down"
          @click="deleteMAR(item)"
          v-if="!directCareStaff && isPageHistory && !isAuditorRole"
        >
          mdi-delete
        </v-icon>

        <v-btn
          outlined
          class="ml-4 hidden-sm-and-up red--text"
          @click="deleteMAR(item)"
          v-if="!directCareStaff && isPageHistory && !isAuditorRole"
        >
          delete
        </v-btn>
      </template>
      <template v-slot:no-data>
        <v-btn color="primary" @click="getMARS()">Reset</v-btn>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
/*eslint-disable */
import { mapGetters, mapActions } from "vuex";
import axios from "axios";
import MarSignDialog from "@/components/medications/MAR/MarSignDialog";

import { setUserTz, setUserSignatureTz, tz } from "../../../../utils/timezone";
const url = require("@/client_config/config").url;

export default {
  components: {
    MarSignDialog,
  },
  props: {
    userSignatureTz: {
      type: Object,
    },
    page: {
      type: String,
      default: "",
    },
    isPageHistory: {
      type: Boolean,
      default: true,
    },
    isAuditorRole: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      headers: [
        {
          text: "Date",
          align: "start",
          sortable: false,
          value: "Date",
        },
        { text: "Medication", value: "Medication.Medication" },
        { text: "Amount Administered", value: "AmountAdministered" },
        { text: "Scheduled Admin Time", value: "AdminTime" },
        { text: "Actual Admin Time", value: "ActualAdminTime" },
        { text: "Notes", value: "Notes" },
        { text: "Exception", value: "Exception" },
        { text: "Signature", value: "Signature", sortable: false },
        { text: "Verified By", value: "VerifiedBy" },
        { text: "Actions", value: "actions", sortable: false },
      ],

      unsignedHeaders: [
        {
          text: "Date",
          align: "start",
          sortable: false,
          value: "Date",
          width: "10%",
        },
        { text: "Medication", value: "Medication.Medication" },
        { text: "Amount Administered", value: "AmountAdministered" },
        { text: "Scheduled Admin Time", value: "AdminTime" },
        // { text: "Signature", value: "Signature", sortable: false },
        { text: "Actions", value: "actions", sortable: false },
      ],
      clientID: this.$route.params.clientID,
      userID: localStorage.getItem("userID"),
      isLoading: false,
      directCareStaff: false,
      dialog: false,
      msg: false,
      mars: [],
      //users: [],
      marItemData: {},
      newVerifiedBy: "",
      search: "",
      staff: localStorage.getItem("Name"),
      text: "",
      color: "",
      itemsPerPage: -1, //Default 10 | -1 display all items
      totalServerItems: 0,
      startMenu: false,
      endMenu: false,
      startDate: "",
      endDate: "",
      unsigned: false
    };
  },

  created() {
    this.getMARS();
  },

  computed: {
    ...mapGetters({
      getUserTimezone: "timezones/getUserTimezone",
      getDefaultTz: "timezones/getDefaultTz",
    }),

    userTimezone() {
      const data = {
        userTimezone: this.getUserTimezone,
        defaultTz: this.getDefaultTz,
      };
      return setUserTz(data);
    },

    tableProps() {
      if (this.page === "history")
        return {
          items: this.mars,
          groupBy: "Date",
          groupDesc: "group-desc",
          loadingText: "Loading MAR History",
          itemClass: this.rowColor,
        };

      return {
        items: this.mars.filter(item => item.Signature === ""), 
        loadingText: "Loading Unsigned",
      };
    },

    userRole() {
      return (
        this.$store.state.users.user.Role === "Administration" ||
        "Program Coordinator" ||
        "Client Services Manager" ||
        "Compliance Manager" ||
        "Human Resources" ||
        "Medical Manager"
      );
    },
  },

  methods: {
    ...mapActions({
      updateMARItem: "mar/updateMARItem",
    }),

    getMARS() {
      this.isLoading = true;
      if (localStorage.getItem("userRole") == "Direct Care Staff" ||
      localStorage.getItem("userRole") == "Client" ||
      localStorage.getItem("userRole") == "Assistant Manager" ||
      localStorage.getItem("userRole") == "Shift Lead" ||
      localStorage.getItem("userRole") == "House Manager") {
        this.directCareStaff = true;
      }
      axios
        .get(`${url}/MAR/${this.clientID}`, {
          params: {
            startDate: this.startDate,
            endDate: this.endDate,
            tz: this.userTimezone
          },
        })
        .then((res) => {
          this.mars = [];
          const { mar } = res.data;
          this.marsTempData = mar;
          console.log(mar);
          this.mars = this.marsTempData.map((item) => {
            return {
              ...item,
              Date: tz(item.Date, this.userTimezone).format("YYYY-MM-DD"),
            };
          });

          // for (var i = 0; i < this.marsTempData.length; i++) {
          //   if (this.marsTempData[i].ClientID == this.clientID) {
          //     // Converts the current Datetime to local time
          //     // this.marsTempData[i].Date = moment(this.marsTempData[i].Date).format('YYYY-MM-DD');
          //     this.marsTempData[i].Date = tz(
          //       this.marsTempData[i].Date,
          //       this.userTimezone
          //     ).format("YYYY-MM-DD");
          //     this.mars.push(this.marsTempData[i]);
          //   }
          // }
        })
        .finally(() => ((this.isLoading = false), this.defaultToggleState()));
      //this.getUsers();
    },

    /*
    getUsers() {
      axios.get(`${url}/users`).then(async (res) => {
        this.users = res.data.users;
        for (var i = 0; i < this.users.length; i++) {
          if (this.userID == this.users[i]._id) {
            this.staff = this.users[i].Name;
          }
        }
      });
    },
    */

    async updateMAR(data, id, unsigned) {
      this.isLoading = true;
      try {
        const response = await this.updateMARItem({ data, id, unsigned});

        this.mars.splice(
          this.mars.findIndex((mar) => mar._id === id),
          1,
          { ...data, _id: id }
        );

        if (response.status == 400)
          return (
            (this.msg = true),
            (this.color = "red"),
            (this.text = response.msg),
            this.close()
          );
        this.color = "pink";
        this.msg = true;
        this.text = "The MAR Item has been updated successfully.";
        this.isLoading = false;
        this.close();
      } catch (error) {
        console.log(error);
      }
    },

    deleteMAR(item) {
      const id = item._id;
      confirm("Are you sure you want to delete this MAR item?") &&
        this.MARDelete(id);
    },

    MARDelete(id) {
      let UrlNotification = this.$router.history.current.path.replaceAll(
        "/",
        "-"
      );
      axios
        .delete(`${url}/MAR/${id}/${this.clientID}/${UrlNotification}`)
        .then((response) => {
          this.getMARS();
          if (response.status == 400) {
            response.json().then(function (data) {
              return data.msg;
            });
          }
          this.msg = true;
          this.text = "The MAR item has been deleted successfully.";
          this.$emit("getMAR");
        });
    },

    verifiedBySignature() {
      this.newVerifiedBy = this.staff + " - " + setUserSignatureTz(this.userSignatureTz);
    },

    viewMAR(item) {
      this.unsigned = item.Signature === "" ? true : false
      this.$refs.marDialog.onViewItem(item);
      this.dialog = true;
    },

    defaultToggleState() {
      let table = this.$refs.table;
      let keys = Object.keys(table.$vnode.componentInstance.openCache);
      keys.forEach((x) => {
        table.$vnode.componentInstance.openCache[x] = false;
      });
    },

    close() {
      this.dialog = false;
    },

    rowColor(item) {
      return item.AdminTime == "PRN" ? "pink lighten-5" : "";
    },
  },
};
</script>
